import { useCallback, useState, useEffect } from "react";

interface UseResendCodeOptions {
  onResend: () => Promise<void> | void;
  initialCountdown?: number;
}

export const useResendCode = ({
  onResend,
  initialCountdown = 120,
}: UseResendCodeOptions) => {
  const [countdown, setCountdown] = useState(initialCountdown);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResend = useCallback(async () => {
    if (countdown > 0 || isResending) return;

    setIsResending(true);
    try {
      await onResend();
      setCountdown(initialCountdown);
    } finally {
      setIsResending(false);
    }
  }, [countdown, isResending, onResend, initialCountdown]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  return {
    countdown,
    canResend: countdown === 0 && !isResending,
    isResending,
    handleResend,
    formatTime,
    resetCountdown: () => setCountdown(initialCountdown),
  };
};
